import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TablePagination,
    Chip,
    Stack,
} from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useDispatch, useSelector } from 'react-redux';
import { getAffiliates, deleteAffiliate } from '../../redux/affiliate';
import {
    selectAffiliates,
    selectAffiliatePagination,
    selectAffiliateLoading,
    selectAffiliateDeleteLoading,
} from '../../redux/affiliate';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { formatDate } from '../../utils/AppUtils';
import DeleteIcon from '../CustomSVGIcons/DeleteIcon';
import { Loading } from '../Loading/Loading';

export interface IAffiliateListModalProps {
    open: boolean;
    onClose: () => void;
}

export const AffiliateListModal: React.FC<IAffiliateListModalProps> = ({ open, onClose }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const affiliates = useSelector(selectAffiliates);
    const pagination = useSelector(selectAffiliatePagination);
    const loading = useSelector(selectAffiliateLoading);
    const deleteLoading = useSelector(selectAffiliateDeleteLoading);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedAffiliateId, setSelectedAffiliateId] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            dispatch(getAffiliates({ page: page + 1, limit: rowsPerPage }));
        }
    }, [open, page, rowsPerPage, dispatch]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeleteClick = (affiliateId: string) => {
        setSelectedAffiliateId(affiliateId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (selectedAffiliateId) {
            dispatch(deleteAffiliate(selectedAffiliateId));
            setDeleteDialogOpen(false);
            setSelectedAffiliateId(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedAffiliateId(null);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    style: {
                        background: theme[themeStyle].secondaryBg,
                        borderRadius: '16px',
                    },
                }}>
                <DialogTitle
                    sx={{
                        color: theme[themeStyle].filters_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '24px',
                        textAlign: 'center',
                        pb: 2,
                    }}>
                    Affiliate Codes
                </DialogTitle>
                {loading && <Loading disableBG />}

                <DialogContent>
                    <Box sx={{ py: 2 }}>
                        <TableContainer component={Paper} sx={{ backgroundColor: theme[themeStyle].secondaryBg }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            User
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Code
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Percentage
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Usage
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Expiration
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {affiliates.map(affiliate => (
                                        <TableRow key={affiliate._id}>
                                            <TableCell>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <ThspianAvatar user={affiliate.user} />
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                color: theme[themeStyle].filters_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            }}>
                                                            {affiliate.user.firstName} {affiliate.user.lastName}
                                                        </Typography>
                                                        <Typography
                                                            sx={{ color: theme[themeStyle].grey, fontSize: '12px' }}>
                                                            @{affiliate.user.userName}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={affiliate.affiliate_code}
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].black,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ color: theme[themeStyle].filters_white }}>
                                                    {affiliate.percentage}%
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ color: theme[themeStyle].filters_white }}>
                                                    {affiliate.usage}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ color: theme[themeStyle].filters_white }}>
                                                    {formatDate(affiliate.expiration)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    onClick={() => handleDeleteClick(affiliate._id)}
                                                    disabled={deleteLoading}
                                                    sx={{
                                                        color: theme[themeStyle].primary_red,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                                        },
                                                    }}>
                                                    <DeleteIcon
                                                        stroke={theme[themeStyle].primary_red}
                                                        width="20px"
                                                        height="20px"
                                                    />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={pagination.totalCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                color: theme[themeStyle].filters_white,
                                '& .MuiTablePagination-selectIcon': {
                                    color: theme[themeStyle].filters_white,
                                },
                                '& .MuiTablePagination-select': {
                                    color: theme[themeStyle].filters_white,
                                },
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <DialogueSheet
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onDisagreeClick={handleDeleteCancel}
                onAgreeClicked={handleDeleteConfirm}
                header="Delete Affiliate Code"
                body="Are you sure you want to delete this affiliate code? This action cannot be undone."
            />
        </>
    );
};
